import useStyles from 'isomorphic-style-loader/useStyles';
import React, { useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Countdown, { zeroPad } from 'react-countdown';
import * as dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import * as Styles from './EventHeader.scss';
import { dispatchSkyfireMethods, preventDefault } from '../utils';
import Template from '../Contexts/Template';
import { getICalURL, getLocalizedEventDate, getLocalizedEventTime, } from '../utils/liveEventDateTime';
import { isMobileUserAgent } from '../utils/platform';
import { useInView } from '../utils/useItemsInView';
import { globals, useSourceCasing } from '../utils/globals';
const getLiveEventState = (startDate, endDate) => {
    dayjs.extend(isBetween);
    const now = dayjs();
    if (now.isBetween(startDate, new Date(endDate.getTime() + 30 * 60000))) {
        return 'live';
    }
    return now.isBefore(startDate) ? 'upcoming' : 'offline';
};
const getPrimaryText = (startDate, endDate, hasMultipleLineups, displayLanguageId, dispatch) => {
    const tz = globals.timezone;
    const formattedLocaleString = displayLanguageId.replace('_', '-');
    // For events with multiple lineups, show start date and end date
    if (hasMultipleLineups) {
        return `${getLocalizedEventDate(startDate, formattedLocaleString, tz, dispatch)} - ${getLocalizedEventDate(endDate, formattedLocaleString, tz, dispatch)}`;
    }
    // For events with just one lineup, show date and time
    return `${getLocalizedEventDate(startDate, formattedLocaleString, tz, dispatch)} ∙ ${getLocalizedEventTime(startDate, formattedLocaleString, tz, dispatch)}`;
};
const countdownRenderer = ({ days, hours, minutes, seconds }) => (React.createElement("div", { className: Styles.counterContainer },
    React.createElement("div", { className: "headline-4", id: Styles.daysText }, zeroPad(days)),
    React.createElement("div", { className: "headline-4", id: Styles.hoursText }, zeroPad(hours)),
    React.createElement("div", { className: "headline-4", id: Styles.minutesText }, zeroPad(minutes)),
    React.createElement("div", { className: "headline-4", id: Styles.secondsText }, zeroPad(seconds))));
const getBtnsForLiveEventState = (liveEventState, btns) => btns.filter((btn) => liveEventState === 'live' ? btn.icon !== 'calendaradd' : btn.icon === 'calendaradd');
/**
 * Filters in/out the "Add to calendar" button (named "calendaradd" in the Bauhaus icon library)
 * according to the live event status.
 *
 * NOTE: This method mirrors the one above but using the live event status returned by the
 * RomeoVoid service. Once the new event detail page experience is launched, the above
 * method will be removed.
 *
 * TODO: Move button filtering logic to server-side
 * https://issues.amazon.com/issues/ALPS-25051
 */
const getButtonsForLiveEventStatus = (status, buttons) => buttons.filter((button) => status === 'UPCOMING' || status === 'BETWEEN'
    ? button.icon === 'calendaradd'
    : button.icon !== 'calendaradd');
export default function EventHeader(props) {
    const { primaryText: headline, secondaryText, tertiaryText, postEventText, calendarDescription, durationUnitText, hasMultipleLineups, image, liveImage, label, buttons, iconButtons, startTime, endTime, daysText, hoursText, minutesText, secondsText, onRefresh, status, nextStartDate, nextEndDate, } = props.data;
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    const nextStart = new Date(nextStartDate);
    const nextEnd = new Date(nextEndDate);
    const callbackRef = useInView(props.onViewed);
    const dispatch = useDispatch();
    const template = useContext(Template);
    const displayLanguageId = useSelector((state) => state.Authentication.displayLanguageId);
    const [liveEventState, setLiveEventState] = useState(getLiveEventState(startDate, endDate));
    const liveEventBtns = getBtnsForLiveEventState(liveEventState, buttons);
    const eventButtons = getButtonsForLiveEventStatus(status, buttons);
    const onClick = useCallback((onItemSelected, event) => {
        if (template) {
            dispatchSkyfireMethods(dispatch, template, onItemSelected);
        }
    }, [dispatch, template]);
    const onCalendarClick = () => {
        dayjs.extend(utc);
        const urlWithRef = new URL(document.URL);
        urlWithRef.searchParams.append('ref', 'dm_aml_cal');
        const icalFormattedStartDate = dayjs(startDate)
            .utc()
            .format('YYYYMMDDTHHmmssZ')
            .replace('+00:00', 'Z');
        const icalFormattedEndDate = dayjs(endDate)
            .utc()
            .format('YYYYMMDDTHHmmssZ')
            .replace('+00:00', 'Z');
        const formattedCalendarDescription = [`${urlWithRef}`, calendarDescription]
            .join('\n\n')
            .replace(/\n/g, '\\n');
        const url = getICalURL(icalFormattedStartDate, icalFormattedEndDate, headline, formattedCalendarDescription);
        if (!isMobileUserAgent()) {
            const filename = `${headline}.ics`;
            const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        else {
            window.open(url, '_blank');
        }
    };
    useStyles(Styles);
    return (React.createElement("div", null,
        (!onRefresh || onRefresh.length === 0) && ( // Original event detail page experience
        React.createElement("div", { className: Styles.eventHeaderContainer },
            React.createElement("div", { className: Styles.eventDetailPageOverlay }),
            React.createElement("music-detail-header", { ref: callbackRef, key: headline, style: {
                    contain: 'layout',
                    marginTop: '24px',
                }, label: label, headline: headline, "primary-text": liveEventState === 'offline'
                    ? postEventText
                    : `${getPrimaryText(startDate, endDate, hasMultipleLineups, displayLanguageId, dispatch)}`, "primary-text-href": null, onmusicPrimaryTextActivate: null, "secondary-text": liveEventState === 'offline' ? null : secondaryText, "secondary-text-href": null, onmusicSecondaryTextActivate: null, onClick: preventDefault, 
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useSourceCasing: useSourceCasing() }),
            React.createElement("div", { className: Styles.eventHeaderDetailContainer },
                React.createElement("div", { className: [
                        Styles.imageContainer,
                        liveEventState === 'offline' ? Styles.offline : '',
                    ].join(' ') },
                    React.createElement("music-image", { src: liveEventState === 'live' ? liveImage : image, alt: headline })),
                liveEventState === 'upcoming' && (React.createElement("div", null,
                    React.createElement("div", { className: Styles.countdownContainer },
                        React.createElement("div", { className: Styles.innerCounterContainer },
                            React.createElement(Countdown, { date: startDate, intervalDelay: 0, precision: 4, renderer: countdownRenderer, onComplete: () => setLiveEventState('live') })),
                        React.createElement("div", { className: Styles.timeTextContainer },
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" },
                                    " ",
                                    daysText,
                                    " ")),
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" }, hoursText)),
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" }, minutesText)),
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" }, secondsText)))),
                    React.createElement("div", { className: Styles.calendarCtaContainer },
                        liveEventBtns.map((btn, index) => (React.createElement("music-button", { key: btn.icon, slot: "buttons", variant: "glass", "icon-name": btn.icon, onClick: () => {
                                onCalendarClick();
                                onClick(btn.onItemSelected);
                            } }, btn.text))),
                        iconButtons.map((btn) => (React.createElement("music-button", { key: btn.icon, "icon-name": btn.icon, slot: "buttons", "icon-only": true, variant: "glass", size: "small", onClick: onClick.bind(this, btn.onItemSelected) })))))),
                liveEventState === 'live' && (React.createElement("div", { className: Styles.ctaContainer },
                    liveEventBtns.map((btn, index) => (React.createElement("music-button", { key: btn.icon, slot: "buttons", variant: index % 2 === 0 ? 'solid' : 'outline', onClick: onClick.bind(this, btn.onItemSelected), href: btn.href }, btn.text))),
                    React.createElement("div", { className: Styles.iconsContainer }, iconButtons.map((btn) => (React.createElement("music-button", { key: btn.icon, iconName: btn.icon, slot: "buttons", iconOnly: true, variant: "glass", size: "small", onClick: onClick.bind(this, btn.onItemSelected) }))))))))),
        onRefresh &&
            onRefresh.length > 0 && ( // New event detail page experience
        React.createElement("div", { className: Styles.eventHeaderContainer },
            React.createElement("div", { className: Styles.eventDetailPageOverlay }),
            React.createElement("music-detail-header", { ref: callbackRef, key: headline, style: {
                    contain: 'layout',
                    marginTop: '24px',
                }, label: label, headline: headline, "primary-text": status === 'AFTERGLOW'
                    ? postEventText
                    : `${getPrimaryText(startDate, endDate, hasMultipleLineups, displayLanguageId, dispatch)}`, "primary-text-href": null, onmusicPrimaryTextActivate: null, "secondary-text": status === 'AFTERGLOW' ? null : secondaryText, "secondary-text-href": null, onmusicSecondaryTextActivate: null, onClick: preventDefault, 
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useSourceCasing: useSourceCasing() }),
            React.createElement("div", { className: Styles.eventHeaderDetailContainer },
                React.createElement("div", { className: [
                        Styles.imageContainer,
                        status === 'AFTERGLOW' ? Styles.offline : '',
                    ].join(' ') },
                    React.createElement("music-image", { src: status === 'LIVE' ? liveImage : image, alt: headline })),
                (status === 'UPCOMING' || status === 'BETWEEN') && (React.createElement("div", null,
                    React.createElement("div", { className: Styles.countdownContainer },
                        React.createElement("div", { className: Styles.innerCounterContainer },
                            React.createElement(Countdown, { date: status === 'UPCOMING' ? startDate : nextStart, intervalDelay: 0, precision: 4, renderer: countdownRenderer, onComplete: () => {
                                    if (template) {
                                        dispatchSkyfireMethods(dispatch, template, onRefresh);
                                    }
                                } })),
                        React.createElement("div", { className: Styles.timeTextContainer },
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" },
                                    ' ',
                                    daysText,
                                    ' ')),
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" }, hoursText)),
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" }, minutesText)),
                            React.createElement("div", { className: Styles.timeLabelText },
                                React.createElement("div", { className: "music-tertiary-text" }, secondsText)))),
                    React.createElement("div", { className: Styles.calendarCtaContainer },
                        eventButtons.map((btn, index) => (React.createElement("music-button", { key: btn.icon, slot: "buttons", variant: "glass", "icon-name": btn.icon, onClick: () => {
                                onCalendarClick();
                                onClick(btn.onItemSelected);
                            } }, btn.text))),
                        iconButtons.map((btn) => (React.createElement("music-button", { key: btn.icon, "icon-name": btn.icon, slot: "buttons", "icon-only": true, variant: "glass", size: "small", onClick: onClick.bind(this, btn.onItemSelected) })))))),
                status === 'LIVE' && (React.createElement("div", null,
                    React.createElement("div", { className: Styles.invisible },
                        React.createElement(Countdown, { date: nextEnd, intervalDelay: 0, precision: 4, renderer: countdownRenderer, onComplete: () => {
                                if (template) {
                                    dispatchSkyfireMethods(dispatch, template, onRefresh);
                                }
                            } })),
                    React.createElement("div", { className: Styles.ctaContainer },
                        eventButtons.map((btn, index) => (React.createElement("music-button", { key: btn.icon, slot: "buttons", variant: index % 2 === 0 ? 'solid' : 'outline', onClick: onClick.bind(this, btn.onItemSelected), href: btn.href }, btn.text))),
                        React.createElement("div", { className: Styles.iconsContainer }, iconButtons.map((btn) => (React.createElement("music-button", { key: btn.icon, iconName: btn.icon, slot: "buttons", iconOnly: true, variant: "glass", size: "small", onClick: onClick.bind(this, btn.onItemSelected) }))))))))))));
}
